import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
	Banknote,
	Check,
	CircleAlert,
	CircleCheck,
	ChevronDown,
	Clock3,
	FileCheck2,
	LoaderCircle,
	Mail,
	Printer,
	Search,
	Upload,
	XCircle,
} from "lucide-react";
import { useMemo, useState } from "react";

import { api, type CifList } from "@/client";
import { MetricStrip } from "@/components/MetricStrip";
import { FinancePresentation } from "@/presentation/finance";

type CifRecord = CifList["records"][number];
type CifRequest = CifList["requests"][number];
type CifTab = "DELIVERIES" | "REQUESTS" | "EMAILS";
type DeliveryStatus = "WAITING_SPLIT" | "IN_PROGRESS" | "COMPLETED" | "REVIEW";
type DeliveryStatusFilter = DeliveryStatus | "ALL";

export function CifView() {
	const queryClient = useQueryClient();
	const [tab, setTab] = useState<CifTab>("DELIVERIES");
	const query = useQuery({
		queryKey: ["cif", "current-month"],
		queryFn: () => api.cif.list(),
	});
	const invalidate = async () => {
		await Promise.all([
			queryClient.invalidateQueries({ queryKey: ["cif", "current-month"] }),
			queryClient.invalidateQueries({ queryKey: ["treasury", "current-month"] }),
			queryClient.invalidateQueries({ queryKey: ["transactions", "current-month"] }),
		]);
	};

	if (query.isPending) {
		return <Loading label="Montando o fluxo CIF…" />;
	}
	if (query.error) {
		return <Failure error={query.error} />;
	}

	const data = query.data;
	const completed = data.records.filter((record) => record.progress.completed).length;
	const waitingSplit = data.records.filter(
		(record) => !record.split || record.progress.expectedDiverged,
	).length;
	const correctionAlerts = data.documentAlerts.length;

	return (
		<>
			<div className="mb-4">
				<MetricStrip
					items={[
						{
							label: "Entregas CIF",
							value: String(data.records.length),
							detail: data.month.label,
						},
						{
							label: "Aguardando divisão",
							value: String(waitingSplit),
							detail: "frete + descarga",
							tone: waitingSplit > 0 ? "attention" : "success",
						},
						{
							label: "CIF concluídos",
							value: String(completed),
							detail: "pagamentos e documentos",
							tone: "success",
						},
						{
							label: "CT-es para revisar",
							value: String(correctionAlerts),
							detail: "correção ou cancelamento",
							tone: correctionAlerts > 0 ? "attention" : "success",
						},
					]}
				/>
			</div>

			<div className="mb-4 flex flex-wrap gap-2">
				<TabButton active={tab === "DELIVERIES"} onClick={() => setTab("DELIVERIES")}>
					Entregas
				</TabButton>
				<TabButton active={tab === "REQUESTS"} onClick={() => setTab("REQUESTS")}>
					Solicitações ({data.requests.length})
				</TabButton>
				<TabButton active={tab === "EMAILS"} onClick={() => setTab("EMAILS")}>
					Documentos e e-mails ({data.documentAlerts.length + data.emailOutbox.length})
				</TabButton>
			</div>

			{tab === "DELIVERIES" ? (
				<Deliveries data={data} invalidate={invalidate} />
			) : tab === "REQUESTS" ? (
				<Requests data={data} invalidate={invalidate} />
			) : (
				<Emails alerts={data.documentAlerts} emails={data.emailOutbox} />
			)}
		</>
	);
}

function Deliveries({ data, invalidate }: { data: CifList; invalidate: () => Promise<void> }) {
	const [freightSelection, setFreightSelection] = useState<Set<string>>(new Set());
	const [cashSelection, setCashSelection] = useState<Set<string>>(new Set());
	const [freightAmounts, setFreightAmounts] = useState<Record<string, string>>({});
	const [cashAmounts, setCashAmounts] = useState<Record<string, string>>({});
	const [expandedId, setExpandedId] = useState<string>();
	const [driverSearch, setDriverSearch] = useState("");
	const [statusFilter, setStatusFilter] = useState<DeliveryStatusFilter>("ALL");
	const requestMutation = useMutation({
		mutationFn: (input: {
			kind: "CHECK" | "CASH";
			allocations: { recordId: string; amountCents: number }[];
		}) => api.cif.requestPayment(input),
		onSuccess: async () => {
			setFreightSelection(new Set());
			setCashSelection(new Set());
			setFreightAmounts({});
			setCashAmounts({});
			await invalidate();
		},
	});
	const allocatedByRecordComponent = useMemo(() => {
		const totals = new Map<string, number>();

		for (const request of data.requests) {
			if (request.supersededBy || request.canceledAt) {
				continue;
			}

			for (const allocation of request.allocations) {
				const key = `${allocation.recordId}:${allocation.component}`;

				totals.set(key, (totals.get(key) ?? 0) + allocation.amountCents);
			}
		}

		return totals;
	}, [data.requests]);
	const visibleRecords = useMemo(() => {
		const search = searchable(driverSearch);

		return data.records
			.filter(
				(record) =>
					(!search || searchable(record.driverName ?? "").includes(search)) &&
					(statusFilter === "ALL" || deliveryStatus(record).id === statusFilter),
			)
			.toSorted((left, right) => Number(right.nfeNumber) - Number(left.nfeNumber));
	}, [data.records, driverSearch, statusFilter]);
	const remaining = (record: CifRecord, component: "FREIGHT" | "UNLOADING") => {
		if (!record.split) {
			return 0;
		}

		const total = component === "FREIGHT" ? record.split.freightCents : record.split.unloadingCents;

		return total - (allocatedByRecordComponent.get(`${record.id}:${component}`) ?? 0);
	};
	const requestSelected = (kind: "CHECK" | "CASH") => {
		const selection = kind === "CHECK" ? freightSelection : cashSelection;
		const amounts = kind === "CHECK" ? freightAmounts : cashAmounts;
		const allocations = data.records.flatMap((record) => {
			const amountCents = inputCents(amounts[record.id] ?? "0");

			return selection.has(record.id) && amountCents > 0
				? [{ recordId: record.id, amountCents }]
				: [];
		});

		requestMutation.mutate({ kind, allocations });
	};

	return (
		<>
			<section className="mb-5 flex flex-col gap-3 rounded-2xl border border-stone-200 bg-white p-4 shadow-sm xl:flex-row xl:items-center xl:justify-between">
				<div>
					<p className="text-sm font-semibold text-stone-900">Agrupar entregas selecionadas</p>
					<p className="mt-1 text-xs text-stone-500">
						{freightSelection.size} para cheque · {cashSelection.size} para espécie
					</p>
				</div>
				<label className="relative min-w-64">
					<span className="sr-only">Buscar cargas por motorista</span>
					<Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-stone-400" />
					<input
						value={driverSearch}
						onChange={(event) => setDriverSearch(event.target.value)}
						placeholder="Buscar por motorista"
						className="h-10 w-full rounded-xl border border-stone-200 bg-white pl-9 pr-3 text-sm"
					/>
				</label>
				<label className="text-xs font-medium text-stone-600">
					Status
					<select
						value={statusFilter}
						onChange={(event) => setStatusFilter(deliveryStatusFilter(event.target.value))}
						className="mt-1 block h-10 rounded-xl border border-stone-200 bg-white px-3 text-sm"
					>
						<option value="ALL">Todos</option>
						<option value="WAITING_SPLIT">Aguardando divisão</option>
						<option value="IN_PROGRESS">Em andamento</option>
						<option value="COMPLETED">Concluído</option>
						<option value="REVIEW">Revisar</option>
					</select>
				</label>
				<div className="flex flex-wrap items-center gap-2">
					<ActionButton
						disabled={freightSelection.size === 0 || requestMutation.isPending}
						onClick={() => requestSelected("CHECK")}
					>
						<Check className="size-4" />
						Solicitar cheque
					</ActionButton>
					<ActionButton
						disabled={cashSelection.size === 0 || requestMutation.isPending}
						onClick={() => requestSelected("CASH")}
					>
						<Banknote className="size-4" />
						Solicitar espécie
					</ActionButton>
				</div>
				{requestMutation.error && (
					<p className="text-xs text-red-700">{requestMutation.error.message}</p>
				)}
			</section>

			<section className="overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-sm">
				{visibleRecords.map((record) => (
					<DeliveryRow
						key={record.id}
						record={record}
						expanded={expandedId === record.id}
						onExpanded={() =>
							setExpandedId((current) => (current === record.id ? undefined : record.id))
						}
						freightRemaining={remaining(record, "FREIGHT")}
						unloadingRemaining={remaining(record, "UNLOADING")}
						freightSelected={freightSelection.has(record.id)}
						cashSelected={cashSelection.has(record.id)}
						freightAmount={
							freightAmounts[record.id] ??
							String(remaining(record, "FREIGHT") / 100).replace(".", ",")
						}
						cashAmount={
							cashAmounts[record.id] ??
							String(remaining(record, "UNLOADING") / 100).replace(".", ",")
						}
						onFreightSelected={(selected) => {
							setFreightSelection(changedSelection(freightSelection, record.id, selected));
							if (selected) {
								setFreightAmounts((current) => ({
									...current,
									[record.id]: String(remaining(record, "FREIGHT") / 100).replace(".", ","),
								}));
							}
						}}
						onCashSelected={(selected) => {
							setCashSelection(changedSelection(cashSelection, record.id, selected));
							if (selected) {
								setCashAmounts((current) => ({
									...current,
									[record.id]: String(remaining(record, "UNLOADING") / 100).replace(".", ","),
								}));
							}
						}}
						onFreightAmount={(value) =>
							setFreightAmounts((current) => ({ ...current, [record.id]: value }))
						}
						onCashAmount={(value) =>
							setCashAmounts((current) => ({ ...current, [record.id]: value }))
						}
						invalidate={invalidate}
					/>
				))}
				{visibleRecords.length === 0 && (
					<p className="p-10 text-center text-sm text-stone-500">
						Nenhuma carga encontrada com os filtros selecionados.
					</p>
				)}
			</section>
		</>
	);
}

function DeliveryRow({
	record,
	expanded,
	onExpanded,
	freightRemaining,
	unloadingRemaining,
	freightSelected,
	cashSelected,
	freightAmount,
	cashAmount,
	onFreightSelected,
	onCashSelected,
	onFreightAmount,
	onCashAmount,
	invalidate,
}: {
	record: CifRecord;
	expanded: boolean;
	onExpanded: () => void;
	freightRemaining: number;
	unloadingRemaining: number;
	freightSelected: boolean;
	cashSelected: boolean;
	freightAmount: string;
	cashAmount: string;
	onFreightSelected: (selected: boolean) => void;
	onCashSelected: (selected: boolean) => void;
	onFreightAmount: (value: string) => void;
	onCashAmount: (value: string) => void;
	invalidate: () => Promise<void>;
}) {
	const [freight, setFreight] = useState(
		record.split ? String(record.split.freightCents / 100).replace(".", ",") : "",
	);
	const [unloading, setUnloading] = useState(
		record.split ? String(record.split.unloadingCents / 100).replace(".", ",") : "0",
	);
	const splitMutation = useMutation({
		mutationFn: () =>
			api.cif.confirmSplit({
				recordId: record.id,
				freightCents: inputCents(freight),
				unloadingCents: inputCents(unloading),
			}),
		onSuccess: invalidate,
	});
	const status = deliveryStatus(record);

	return (
		<article className="border-b border-stone-100 last:border-b-0">
			<div className="grid gap-3 px-4 py-3.5 xl:grid-cols-[minmax(250px,1.25fr)_145px_126px_minmax(185px,0.9fr)_120px_36px] xl:items-center">
				<div className="min-w-0">
					<h3 className="truncate text-sm font-semibold text-stone-950">
						NF-e {record.nfeNumber} · {record.customerName}
					</h3>
					<p className="mt-1 truncate text-xs text-stone-500">
						{FinancePresentation.transactionDate(record.occurredOn)} ·{" "}
						{record.driverName ?? "Motorista não informado"} · {record.plate} ·{" "}
						{record.destinationCity ?? "Destino não informado"}
					</p>
				</div>
				<div>
					<span
						className={`inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold ${status.className}`}
					>
						{status.label}
					</span>
				</div>
				<DeliveryProgress record={record} />
				<div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1.5 text-[11px] font-medium text-stone-700">
					{record.split &&
						!record.progress.sourceDiverged &&
						freightRemaining <= 0 &&
						unloadingRemaining <= 0 && <span className="text-stone-400">Nada a solicitar</span>}
					{record.split && !record.progress.sourceDiverged && freightRemaining > 0 && (
						<label className="flex items-center gap-1.5">
							<input
								type="checkbox"
								checked={freightSelected}
								onChange={(event) => onFreightSelected(event.target.checked)}
							/>
							Cheque
						</label>
					)}
					{record.split &&
						!record.progress.sourceDiverged &&
						freightRemaining > 0 &&
						freightSelected && (
							<input
								value={freightAmount}
								onChange={(event) => onFreightAmount(event.target.value)}
								inputMode="decimal"
								aria-label={`Valor do cheque da NF-e ${record.nfeNumber}`}
								className="h-7 w-24 rounded-lg border border-stone-200 px-2 text-[11px]"
							/>
						)}
					{record.split && !record.progress.sourceDiverged && unloadingRemaining > 0 && (
						<label className="flex items-center gap-1.5">
							<input
								type="checkbox"
								checked={cashSelected}
								onChange={(event) => onCashSelected(event.target.checked)}
							/>
							Espécie
						</label>
					)}
					{record.split &&
						!record.progress.sourceDiverged &&
						unloadingRemaining > 0 &&
						cashSelected && (
							<input
								value={cashAmount}
								onChange={(event) => onCashAmount(event.target.value)}
								inputMode="decimal"
								aria-label={`Valor em espécie da NF-e ${record.nfeNumber}`}
								className="h-7 w-24 rounded-lg border border-stone-200 px-2 text-[11px]"
							/>
						)}
					{!record.split && <span className="text-stone-400">Divisão necessária</span>}
				</div>
				<p className="text-right text-sm font-semibold tabular-nums text-stone-950">
					{FinancePresentation.money(record.expectedCents)}
				</p>
				<button
					type="button"
					onClick={onExpanded}
					aria-expanded={expanded}
					aria-label={`${expanded ? "Fechar" : "Abrir"} detalhes da NF-e ${record.nfeNumber}`}
					className="grid size-9 place-items-center rounded-xl text-stone-500 transition hover:bg-stone-100 hover:text-stone-900"
				>
					<ChevronDown className={`size-4 transition-transform ${expanded ? "rotate-180" : ""}`} />
				</button>
			</div>

			{expanded && (
				<div className="border-t border-stone-100 bg-stone-50/70 px-4 py-4">
					<div className="grid gap-4 xl:grid-cols-[minmax(320px,0.9fr)_minmax(360px,1.1fr)]">
						<div className="rounded-xl border border-stone-200 bg-white p-3">
							<div className="grid gap-2 sm:grid-cols-2">
								<label className="text-xs font-medium text-stone-600">
									Frete
									<input
										value={freight}
										onChange={(event) => setFreight(event.target.value)}
										inputMode="decimal"
										disabled={record.progress.sourceDiverged}
										className="mt-1 h-10 w-full rounded-xl border border-stone-200 bg-white px-3 text-sm"
									/>
								</label>
								<label className="text-xs font-medium text-stone-600">
									Descarga
									<input
										value={unloading}
										onChange={(event) => setUnloading(event.target.value)}
										inputMode="decimal"
										disabled={record.progress.sourceDiverged}
										className="mt-1 h-10 w-full rounded-xl border border-stone-200 bg-white px-3 text-sm"
									/>
								</label>
							</div>
							<div className="mt-2 flex flex-wrap items-end gap-2">
								<ActionButton
									disabled={record.progress.sourceDiverged || splitMutation.isPending}
									onClick={() => splitMutation.mutate()}
								>
									{splitMutation.isPending ? (
										<LoaderCircle className="size-4 animate-spin" />
									) : (
										<CircleCheck className="size-4" />
									)}
									{record.split ? "Corrigir divisão" : "Confirmar divisão"}
								</ActionButton>
							</div>
							{splitMutation.error && (
								<p className="mt-2 text-xs text-red-700">{splitMutation.error.message}</p>
							)}
						</div>
						<div>
							<p className="mb-2 text-xs text-stone-500">
								Representante: {record.representativeName ?? "não informado"}
							</p>
							<div className="grid grid-cols-2 gap-2 text-xs sm:grid-cols-3">
								<Fact label="Cheque solicitado" complete={record.progress.freightRequested} />
								<Fact label="Cheque compensado" complete={record.progress.freightSettled} />
								<Fact label="Espécie solicitada" complete={record.progress.unloadingRequested} />
								<Fact label="Espécie entregue" complete={record.progress.unloadingSettled} />
								<Fact label="CT-e do frete" complete={record.progress.freightDocumentLinked} />
								<Fact label="CT-e complemento" complete={record.progress.unloadingDocumentLinked} />
							</div>
						</div>
					</div>
					{record.progress.expectedDiverged && (
						<p className="mt-3 rounded-xl bg-red-50 px-3 py-2 text-xs font-medium text-red-800">
							O valor CIF da Expedição mudou depois da divisão. Confirme novamente frete e descarga.
						</p>
					)}
					{record.progress.sourceDiverged && (
						<p className="mt-3 rounded-xl bg-red-50 px-3 py-2 text-xs font-medium text-red-800">
							O item deixou de estar elegível na Expedição. O histórico foi preservado, mas novas
							decisões financeiras estão bloqueadas até a origem voltar a confirmar o CIF.
						</p>
					)}
				</div>
			)}
		</article>
	);
}

function DeliveryProgress({ record }: { record: CifRecord }) {
	const facts = [
		["Cheque solicitado", record.progress.freightRequested],
		["Cheque compensado", record.progress.freightSettled],
		["Espécie solicitada", record.progress.unloadingRequested],
		["Espécie entregue", record.progress.unloadingSettled],
		["CT-e do frete", record.progress.freightDocumentLinked],
		["CT-e complemento", record.progress.unloadingDocumentLinked],
	] as const;

	return (
		<div className="flex items-center gap-1" aria-label="Progresso da entrega">
			{facts.map(([label, complete]) => (
				<span
					key={label}
					title={label}
					className={`grid size-5 place-items-center rounded-full ${
						complete ? "bg-emerald-50 text-emerald-700" : "bg-stone-100 text-stone-400"
					}`}
				>
					{complete ? <CircleCheck className="size-3.5" /> : <Clock3 className="size-3.5" />}
					<span className="sr-only">
						{label}: {complete ? "concluído" : "pendente"}
					</span>
				</span>
			))}
		</div>
	);
}

function Requests({ data, invalidate }: { data: CifList; invalidate: () => Promise<void> }) {
	return (
		<section className="space-y-3">
			{data.requests.map((request) => (
				<RequestCard key={request.id} request={request} invalidate={invalidate} />
			))}
			{data.requests.length === 0 && (
				<p className="rounded-2xl border border-stone-200 bg-white p-10 text-center text-sm text-stone-500">
					Nenhuma solicitação criada.
				</p>
			)}
		</section>
	);
}

function RequestCard({
	request,
	invalidate,
}: {
	request: CifRequest;
	invalidate: () => Promise<void>;
}) {
	const [checkNumber, setCheckNumber] = useState("");
	const [cashAccountId, setCashAccountId] = useState<"caixa-amanda" | "caixa-sindi">(
		"caixa-amanda",
	);
	const [occurredOn, setOccurredOn] = useState(new Intl.DateTimeFormat("en-CA").format(new Date()));
	const issueMutation = useMutation({
		mutationFn: () => api.cif.issueCheck({ requestId: request.id, checkNumber }),
		onSuccess: invalidate,
	});
	const cashMutation = useMutation({
		mutationFn: () =>
			api.cif.disburseCash({
				requestId: request.id,
				cashAccountId,
				occurredOn,
			}),
		onSuccess: invalidate,
	});
	const reissueMutation = useMutation({
		mutationFn: () => api.cif.reissueReturnedCheck({ requestId: request.id }),
		onSuccess: invalidate,
	});
	const cancelMutation = useMutation({
		mutationFn: () => api.cif.cancelPaymentRequest({ requestId: request.id }),
		onSuccess: invalidate,
	});
	const cancel = () => {
		if (window.confirm("Cancelar esta solicitação e liberar novamente as cargas?")) {
			cancelMutation.mutate();
		}
	};
	const [uploading, setUploading] = useState(false);
	const uploadSigned = async (file: File) => {
		setUploading(true);
		const form = new FormData();

		form.set("requestId", request.id);
		form.set("file", file);
		const response = await fetch("/api/cif-receipt", { method: "POST", body: form });

		setUploading(false);
		if (!response.ok) {
			throw new Error(await response.text());
		}
		await invalidate();
	};
	const state = request.canceledAt
		? "Cancelado"
		: request.kind === "CHECK"
			? request.supersededBy
				? "Substituído"
				: request.returned
					? "Devolvido"
					: request.compensated
						? "Compensado"
						: request.checkNumber
							? "Emitido"
							: "Solicitado"
			: request.disbursedAt
				? "Entregue"
				: "Solicitado";

	return (
		<article className="rounded-2xl border border-stone-200 bg-white p-5 shadow-sm">
			<div className="flex flex-col justify-between gap-3 lg:flex-row lg:items-start">
				<div>
					<div className="flex items-center gap-2">
						{request.kind === "CHECK" ? (
							<Check className="size-4 text-sky-700" />
						) : (
							<Banknote className="size-4 text-emerald-700" />
						)}
						<h3 className="font-semibold text-stone-950">
							{request.kind === "CHECK" ? "Cheque" : "Espécie"} · {state}
						</h3>
					</div>
					<p className="mt-1 text-xs text-stone-500">
						Solicitado por {request.requestedBy} · {request.allocations.length}{" "}
						{request.allocations.length === 1 ? "entrega" : "entregas"}
					</p>
				</div>
				<p className="text-xl font-semibold tabular-nums text-stone-950">
					{FinancePresentation.money(request.totalCents)}
				</p>
			</div>

			<div className="mt-4 flex flex-wrap gap-2">
				{request.allocations.map((allocation) => (
					<span
						key={`${allocation.recordId}:${allocation.component}`}
						className="rounded-lg bg-stone-100 px-2.5 py-1 text-xs font-medium text-stone-600"
					>
						{FinancePresentation.money(allocation.amountCents)}
					</span>
				))}
			</div>

			{request.kind === "CHECK" && !request.checkNumber && !request.canceledAt && (
				<div className="mt-4 flex flex-wrap items-end gap-2 border-t border-stone-100 pt-4">
					<label className="text-xs font-medium text-stone-600">
						Número do cheque
						<input
							value={checkNumber}
							onChange={(event) => setCheckNumber(event.target.value)}
							className="mt-1 block h-10 rounded-xl border border-stone-200 px-3"
						/>
					</label>
					<ActionButton
						disabled={!checkNumber || issueMutation.isPending}
						onClick={() => issueMutation.mutate()}
					>
						Registrar emissão
					</ActionButton>
					<ActionButton tone="danger" disabled={cancelMutation.isPending} onClick={cancel}>
						<XCircle className="size-4" />
						Cancelar solicitação
					</ActionButton>
				</div>
			)}

			{request.kind === "CHECK" && request.checkNumber && (
				<p className="mt-4 rounded-xl bg-sky-50 px-3 py-2 text-sm font-semibold text-sky-900">
					Cheque nº {request.checkNumber}
					{request.issuedBy ? ` · emitido por ${request.issuedBy}` : ""}
				</p>
			)}

			{request.kind === "CHECK" && request.returned && !request.supersededBy && (
				<div className="mt-4 flex flex-wrap items-end gap-2 border-t border-red-100 pt-4">
					<ActionButton
						disabled={reissueMutation.isPending}
						onClick={() => reissueMutation.mutate()}
					>
						Solicitar novo cheque
					</ActionButton>
					<p className="self-center text-xs text-red-700">
						A solicitação anterior será preservada como devolvida.
					</p>
				</div>
			)}

			{request.kind === "CASH" && !request.disbursedAt && !request.canceledAt && (
				<div className="mt-4 flex flex-wrap items-end gap-2 border-t border-stone-100 pt-4">
					<label className="text-xs font-medium text-stone-600">
						Caixa
						<select
							value={cashAccountId}
							onChange={(event) =>
								setCashAccountId(
									event.target.value === "caixa-sindi" ? "caixa-sindi" : "caixa-amanda",
								)
							}
							className="mt-1 block h-10 rounded-xl border border-stone-200 bg-white px-3"
						>
							<option value="caixa-amanda">Caixa Amanda</option>
							<option value="caixa-sindi">Caixa Sindi</option>
						</select>
					</label>
					<label className="text-xs font-medium text-stone-600">
						Data da saída
						<input
							type="date"
							value={occurredOn}
							onChange={(event) => setOccurredOn(event.target.value)}
							className="mt-1 block h-10 rounded-xl border border-stone-200 px-3"
						/>
					</label>
					<ActionButton disabled={cashMutation.isPending} onClick={() => cashMutation.mutate()}>
						Registrar saída
					</ActionButton>
					<ActionButton tone="danger" disabled={cancelMutation.isPending} onClick={cancel}>
						<XCircle className="size-4" />
						Cancelar solicitação
					</ActionButton>
				</div>
			)}

			{request.canceledAt && (
				<p className="mt-4 rounded-xl bg-red-50 px-3 py-2 text-sm font-semibold text-red-800">
					Cancelado por {request.canceledBy} · {FinancePresentation.moment(request.canceledAt)}
				</p>
			)}

			{request.kind === "CASH" && request.disbursedAt && (
				<div className="mt-4 flex flex-wrap items-center gap-2 border-t border-stone-100 pt-4">
					<a
						href={`/api/cif-receipt?requestId=${encodeURIComponent(request.id)}&kind=GENERATED`}
						target="_blank"
						rel="noreferrer"
						className="inline-flex h-10 items-center gap-2 rounded-xl bg-stone-900 px-3 text-xs font-semibold text-white"
					>
						<Printer className="size-4" />
						Imprimir recibo
					</a>
					<label className="inline-flex h-10 cursor-pointer items-center gap-2 rounded-xl border border-stone-200 px-3 text-xs font-semibold text-stone-700">
						<Upload className="size-4" />
						{uploading ? "Armazenando…" : "Anexar assinado"}
						<input
							type="file"
							accept="application/pdf,image/jpeg,image/png"
							className="hidden"
							disabled={uploading}
							onChange={(event) => {
								const file = event.target.files?.[0];

								if (file) {
									void uploadSigned(file);
								}
							}}
						/>
					</label>
					{request.receipts.some((receipt) => receipt.kind === "SIGNED") && (
						<a
							href={`/api/cif-receipt?requestId=${encodeURIComponent(request.id)}&kind=SIGNED`}
							target="_blank"
							rel="noreferrer"
							className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-800"
						>
							<FileCheck2 className="size-4" />
							Recibo assinado
						</a>
					)}
				</div>
			)}

			{(issueMutation.error ||
				cashMutation.error ||
				reissueMutation.error ||
				cancelMutation.error) && (
				<p className="mt-2 text-xs text-red-700">
					{issueMutation.error?.message ??
						cashMutation.error?.message ??
						reissueMutation.error?.message ??
						cancelMutation.error?.message}
				</p>
			)}
		</article>
	);
}

function Emails({
	alerts,
	emails,
}: {
	alerts: CifList["documentAlerts"];
	emails: CifList["emailOutbox"];
}) {
	return (
		<section className="space-y-3">
			{alerts.map((alert) => (
				<article
					key={alert.id}
					className="rounded-2xl border border-amber-200 bg-amber-50 p-5 shadow-sm"
				>
					<div className="flex items-center gap-2">
						<CircleAlert className="size-4 text-amber-800" />
						<h3 className="font-semibold text-stone-950">Revisar CT-e da NF-e {alert.nfeNumber}</h3>
					</div>
					<p className="mt-2 text-sm text-stone-700">
						{alert.carrierName ?? "Transportadora"} ·{" "}
						{alert.component === "FREIGHT" ? "frete" : "complemento de descarga"} · esperado{" "}
						{FinancePresentation.money(alert.expectedCents)}
						{alert.observedCents == null
							? " · documentos ambíguos"
							: ` · emitido ${FinancePresentation.money(alert.observedCents)}`}
					</p>
				</article>
			))}
			{emails.map((email) => (
				<article
					key={email.id}
					className="rounded-2xl border border-stone-200 bg-white p-5 shadow-sm"
				>
					<div className="flex flex-wrap items-start justify-between gap-3">
						<div>
							<div className="flex items-center gap-2">
								<Mail className="size-4 text-emerald-800" />
								<h3 className="font-semibold text-stone-950">{email.subject}</h3>
							</div>
							<p className="mt-1 text-xs text-stone-500">
								Preparado por {email.createdBy} · {email.to ?? "destinatário não configurado"}
							</p>
						</div>
						<span
							className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${
								email.deliveryState === "SENT"
									? "bg-emerald-100 text-emerald-800"
									: email.approvalRequired
										? "bg-amber-100 text-amber-800"
										: "bg-stone-100 text-stone-700"
							}`}
						>
							{email.deliveryState === "SENT"
								? "Enviado"
								: email.approvalRequired
									? "Revisar antes de enviar"
									: email.deliveryState === "WAITING_CONFIGURATION"
										? "Aguardando configuração"
										: "Rascunho preparado"}
						</span>
					</div>
					<pre className="mt-4 whitespace-pre-wrap rounded-xl bg-stone-50 p-4 font-sans text-xs leading-5 text-stone-700">
						{email.body}
					</pre>
				</article>
			))}
			{alerts.length === 0 && emails.length === 0 && (
				<p className="rounded-2xl border border-stone-200 bg-white p-10 text-center text-sm text-stone-500">
					Nenhum e-mail preparado.
				</p>
			)}
		</section>
	);
}

function Fact({ label, complete }: { label: string; complete: boolean }) {
	return (
		<div
			className={`flex items-center gap-2 rounded-xl px-3 py-2 ${
				complete ? "bg-emerald-50 text-emerald-800" : "bg-stone-100 text-stone-500"
			}`}
		>
			{complete ? <CircleCheck className="size-4" /> : <Clock3 className="size-4" />}
			{label}
		</div>
	);
}

function ActionButton({
	disabled,
	onClick,
	children,
	tone = "primary",
}: {
	disabled?: boolean;
	onClick: () => void;
	children: React.ReactNode;
	tone?: "primary" | "danger";
}) {
	return (
		<button
			type="button"
			disabled={disabled}
			onClick={onClick}
			className={`inline-flex h-10 items-center justify-center gap-2 rounded-xl px-3 text-xs font-semibold transition disabled:cursor-not-allowed disabled:opacity-40 ${
				tone === "danger"
					? "border border-red-200 bg-white text-red-700 hover:bg-red-50"
					: "bg-emerald-950 text-white hover:bg-emerald-900"
			}`}
		>
			{children}
		</button>
	);
}

function TabButton({
	active,
	onClick,
	children,
}: {
	active: boolean;
	onClick: () => void;
	children: React.ReactNode;
}) {
	return (
		<button
			type="button"
			onClick={onClick}
			className={`rounded-xl px-4 py-2.5 text-sm font-semibold ${
				active ? "bg-stone-900 text-white" : "bg-white text-stone-600 shadow-sm"
			}`}
		>
			{children}
		</button>
	);
}

function Loading({ label }: { label: string }) {
	return (
		<div className="grid min-h-80 place-items-center rounded-3xl border border-stone-200 bg-white text-stone-600">
			<div className="flex items-center gap-3 text-sm font-medium">
				<LoaderCircle className="size-5 animate-spin" />
				{label}
			</div>
		</div>
	);
}

function Failure({ error }: { error: Error }) {
	return (
		<div className="rounded-3xl border border-red-200 bg-white p-8 text-center shadow-sm">
			<CircleAlert className="mx-auto mb-3 size-8 text-red-600" />
			<h2 className="font-semibold text-stone-950">Não foi possível abrir o CIF</h2>
			<p className="mt-2 text-sm text-stone-600">{error.message}</p>
		</div>
	);
}

function changedSelection(current: Set<string>, id: string, selected: boolean) {
	const next = new Set(current);

	if (selected) {
		next.add(id);
	} else {
		next.delete(id);
	}

	return next;
}

function inputCents(value: string) {
	const normalized = value.trim().replace(/\./gu, "").replace(",", ".");
	const cents = Math.round(Number(normalized) * 100);

	if (!Number.isSafeInteger(cents) || cents < 0) {
		throw new Error("Informe um valor válido");
	}

	return cents;
}

function searchable(value: string) {
	return value
		.normalize("NFD")
		.replace(/\p{Mark}/gu, "")
		.toLocaleLowerCase("pt-BR")
		.trim();
}

function deliveryStatus(record: CifRecord) {
	if (record.progress.completed) {
		return {
			id: "COMPLETED" as const,
			label: "Concluído",
			className: "bg-emerald-100 text-emerald-800",
		};
	}
	if (record.progress.sourceDiverged) {
		return {
			id: "REVIEW" as const,
			label: "Revisar origem",
			className: "bg-red-100 text-red-800",
		};
	}
	if (record.progress.expectedDiverged) {
		return {
			id: "REVIEW" as const,
			label: "Revisar divisão",
			className: "bg-red-100 text-red-800",
		};
	}
	if (!record.split) {
		return {
			id: "WAITING_SPLIT" as const,
			label: "Aguardando divisão",
			className: "bg-amber-100 text-amber-800",
		};
	}

	return {
		id: "IN_PROGRESS" as const,
		label: "Em andamento",
		className: "bg-sky-100 text-sky-800",
	};
}

function deliveryStatusFilter(value: string): DeliveryStatusFilter {
	switch (value) {
		case "ALL":
		case "WAITING_SPLIT":
		case "IN_PROGRESS":
		case "COMPLETED":
		case "REVIEW":
			return value;
		default:
			throw new Error("Status inválido");
	}
}
